<template>
  <div class="user-shell-page">
    <header class="topbar">
      <div class="topbar-inner">
        <button type="button" class="brand" @click="go('/home')">
          <img class="brand-logo" :src="brandLogo" alt="COG" />
        </button>

        <nav class="top-nav">
          <button v-for="item in navItems" :key="item.label" type="button" :class="['top-nav-item', { active: item.active }]" @click="go(item.path)">
            {{ item.label }}
          </button>
        </nav>

        <div class="top-actions">
          <div
            class="center-entry"
            @mouseenter="profileMenuVisible = true"
            @mouseleave="profileMenuVisible = false"
          >
            <button type="button" class="center-link" @click="go(centerPath)">
              <img class="center-avatar" :src="displayAvatar" alt="头像" referrerpolicy="no-referrer" />
              <span>个人中心</span>
            </button>
            <div v-show="profileMenuVisible" class="center-menu">
              <button type="button" class="center-menu-item" :disabled="loggingOut" @click="handleLogout">
                {{ loggingOut ? '退出中...' : '退出登录' }}
              </button>
            </div>
          </div>
          <button type="button" class="apply-btn" @click="openApplyDialog">立即申请</button>
        </div>
      </div>
    </header>

    <main class="page-body">
      <div class="page-inner">
        <aside class="sidebar">
          <div class="profile-card" :style="profileCardStyle">
            <img class="profile-avatar" :src="displayAvatar" alt="头像" referrerpolicy="no-referrer" />
            <div class="profile-meta">
              <div class="profile-name">{{ profile.name }}</div>
              <div class="profile-role">{{ profileRoleText }}</div>
            </div>
          </div>

          <div class="menu-card">
            <button
              v-for="item in menuItems"
              :key="item.key"
              type="button"
              :class="['menu-item', { active: item.key === activeKey }]"
              @click="go(item.path)"
            >
              <img class="menu-icon" :src="item.key === activeKey ? item.activeIcon : item.inactiveIcon || item.activeIcon" :alt="item.label" />
              <span>{{ item.label }}</span>
            </button>
          </div>
        </aside>

        <section class="content-card">
          <div class="content-header">
            <h1>{{ title }}</h1>
            <p v-if="subtitle">{{ subtitle }}</p>
          </div>
          <slot />
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import { logoutMemberSession } from '@/api/user';
import brandLogo from '@/assets/img/logo.png';
import defaultAvatar from '@/assets/img/user/avatar.png';
import { clearUserSession } from '@/utils/auth';
import { USER_INFO_UPDATED_EVENT } from '@/utils/userInfo';

export default {
  name: 'UserShell',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    profile: {
      type: Object,
      required: true
    },
    menuItems: {
      type: Array,
      default: () => []
    },
    activeKey: {
      type: String,
      default: ''
    },
    centerPath: {
      type: String,
      default: '/user/personal'
    }
  },
  computed: {
    brandLogo() {
      return brandLogo;
    },
    displayAvatar() {
      return this.syncedAvatar || this.profile.avatar || defaultAvatar;
    },
    isMcnRole() {
      const role = String(this.profile && this.profile.role ? this.profile.role : '');
      return role.includes('MCN') || role.includes('机构');
    },
    profileRoleText() {
      const role = String(this.profile && this.profile.role ? this.profile.role : '').trim();
      if (!role) {
        return '--';
      }
      if (role.includes('机构') || role.endsWith('主播')) {
        return role;
      }
      return `${role}主播`;
    },
    profileCardStyle() {
      if (!this.profile.background) {
        return {};
      }
      return {
        backgroundImage: `url(${this.profile.background})`
      };
    },
    navItems() {
      return [
        { label: '首页', path: '/home', active: false },
        { label: '任务', path: '/home', active: false },
        { label: '主播', path: '/home', active: false },
        { label: '扶持政策', path: '/home', active: false },
        { label: '数字资产', path: '/home', active: false },
        { label: '金融服务', path: '/home', active: false }
      ];
    }
  },
  data() {
    return {
      loggingOut: false,
      profileMenuVisible: false,
      syncedAvatar: ''
    };
  },
  created() {
    window.addEventListener(USER_INFO_UPDATED_EVENT, this.handleUserInfoUpdated);
  },
  beforeDestroy() {
    window.removeEventListener(USER_INFO_UPDATED_EVENT, this.handleUserInfoUpdated);
  },
  methods: {
    handleUserInfoUpdated(event) {
      const userInfo = event && event.detail;
      if (userInfo && userInfo.avatar) {
        this.syncedAvatar = userInfo.avatar;
      }
    },
    openApplyDialog() {
      this.$openApplyDialog({
        source: 'user-shell-apply',
        roleType: this.isMcnRole ? 'MCN机构' : '个人主播',
        organization: this.isMcnRole ? this.profile.name : ''
      });
    },
    go(path) {
      if (path && this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    async handleLogout() {
      if (this.loggingOut) {
        return;
      }

      this.loggingOut = true;
      try {
        await logoutMemberSession();
        if (this.$store) {
          await this.$store.dispatch('user/resetToken');
        }
        clearUserSession();
        this.profileMenuVisible = false;
        this.$message.success('已退出登录');
        this.$router.replace('/home');
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '退出登录失败');
      } finally {
        this.loggingOut = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.user-shell-page {
  min-height: 100vh;
  background: #eef3fb;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  height: 78px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 1px 0 rgba(229, 235, 247, 0.95);
}

.topbar-inner {
  width: 1166px;
  max-width: calc(100vw - 32px);
  height: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
}

.brand {
  border: 0;
  padding: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.brand-logo {
  width: 273px;
  display: block;
}

.brand-text {
  color: #222942;
  font-size: 15px;
  font-weight: 700;
}

.top-nav {
  display: flex;
  align-items: center;
  gap: 36px;
  margin-left: 130px;
  width: 450px;
}

.top-nav-item {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 400;
  font-size: 15px;
  color: #646199;
  line-height: 24px;
}

.top-nav-item.active,
.top-nav-item:hover {
  color: #625AE8;
}

.top-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 18px;
}

.center-entry {
  position: relative;
}

.center-link {
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 500;
  font-size: 15px;
  color: #5B63F3;
  line-height: 22px;
}

.center-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  min-width: 118px;
  padding: 8px;
  border: 1px solid #e5e9f7;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(72, 84, 132, 0.14);
}

.center-menu-item {
  width: 100%;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #303a62;
  font-size: 14px;
  cursor: pointer;
}

.center-menu-item:hover:not(:disabled) {
  background: #f3f5ff;
  color: #5b63f3;
}

.center-menu-item:disabled {
  cursor: not-allowed;
  color: #a0a7c2;
}



.center-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  /* 渐变边框颜色（这里用 conic-gradient 示例） */
  background: rgba(93,96,106,0.5);
  background: conic-gradient(from 0deg, #9F64FF, #528AFF);
  /* 关键：用 padding 撑出边框宽度 */
  padding: 2px;
  /* 让 padding 在宽高内计算 */
  box-sizing: border-box;
  position: relative;
}

/* 伪元素作为内层遮罩 */
.center-avatar::after {
  content: '';
  position: absolute;
  /* 因为父元素有 2px padding，所以内层从 padding 边缘开始 */
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  border-radius: 50%;
  background: #fff; /* 此处换成你需要的内部背景色（或透明） */
}

.apply-btn {
	border: none;
 width: 120px;
 height: 44px;
 background: linear-gradient( 117deg, #518BFF 0%, #9E5DFF 100%);
 box-shadow: 0px 4px 14px 0px rgba(99,102,241,0.35);
 border-radius: 10px 10px 10px 10px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.page-body {
  padding: 24px 0 40px;
}

.page-inner {
  width: 1166px;
  max-width: calc(100vw - 32px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 254px minmax(0, 1fr);
  gap: 28px;
  
}

.sidebar {
  align-self: start;
  width: 254px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 3px 8px 0px rgba(118,120,144,0.22);
}


.content-card {
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 3px 8px 0px rgba(118,120,144,0.22);
}

.profile-card {
  min-height: 118px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  background-size: cover;
  background-position: center;
  border-radius: 8px 8px 0 0 ;
}

.profile-avatar {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  object-fit: cover;
  /* 渐变边框颜色（这里用 conic-gradient 示例） */
  background: rgba(93,96,106,0.5);
  background: conic-gradient(from 0deg, #9F64FF, #528AFF);
  /* 关键：用 padding 撑出边框宽度 */
  padding: 2px;
  /* 让 padding 在宽高内计算 */
  box-sizing: border-box;
  position: relative;
}

.profile-meta {
  min-width: 0;
}

.profile-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1d233e;
  font-size: 18px;
  font-weight: 700;
}

.profile-role {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 8px;
  color: #6170ff;
  font-size: 15px;
}

.menu-card {
  min-height: 430px;
  padding: 12px;
  border-radius: 0;
}

.menu-item {
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px;
  font-weight: 400;
  font-size: 14px;
  color: #696E83;
  cursor: pointer;
  width: 230px;
  height: 40px;
  border-radius: 8px 8px 8px 8px;
  margin-bottom: 8px;
}

.menu-item.active {
  background: #E6E8FF;
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 500;
  font-size: 14px;
  color: #4F46E5;
}

.menu-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.content-card {
  min-height: 886px;
  padding: 22px 44px;
  box-shadow: 0px 4px 10px 0px rgba(118,120,144,0.22);
}

.content-header h1 {
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 700;
  font-size: 20px;
  color: #1E293B;
}

.content-header p {
  margin-top: 6px;
 
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 400;
  font-size: 14px;
  color: #999FB8;
}

@media (max-width: 1200px) {
  .topbar {
    height: auto;
    padding: 12px 0;
  }

  .topbar-inner,
  .page-inner {
    max-width: calc(100vw - 24px);
  }

  .topbar-inner {
    flex-wrap: wrap;
    gap: 12px;
  }

  .top-nav {
    margin-left: 0;
    order: 3;
    flex-wrap: wrap;
  }

  .page-inner {
    grid-template-columns: 1fr;
  }
}
</style>
